<!-- eslint-disable vue/multi-word-component-names -->
<script lang="ts">
    // @ts-nocheck
    import tabIcon from "@/assets/vardplatsoversikt.svg";

    export default {
        name: "Vardplatsoversikt",
        tabName: "Vårdplatsöversikt",
        icon: tabIcon,
        refreshTab: true,
    };
</script>

<script setup lang="ts">
    import { ref, toRefs, computed, onBeforeMount, onBeforeUnmount, onMounted, onUnmounted } from "vue";
    import moment from "moment";
    import { getConfig, klinikenApi } from "@/api";
    import { openDialog } from "@/utils";
    import store from "@/store";
    import SearchFilter from "@/components/SearchFilter.vue";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import KButton from "@/components/Button.vue";
    import ActionIconBoka from "@/assets/actionIcons/bokaoperation.svg";
    import ActionIconVardplatsbestallning from "@/assets/vardplatsbestallning.svg";
    import ActionIconStryk from "@/assets/stryk.svg";
    import ActionIconSkrivIn from "@/assets/skriv_in_patient.svg";
    import ActionIconSkrivUt from "@/assets/skriv_ut_patient.svg";
    import StatusBestalldIcon from "@/assets/status_bestalld.svg";
    import StatusBokadIcon from "@/assets/statusbokad.svg";
    import StatusInskrivenIcon from "@/assets/status_inskriven.svg";
    import StatusUtskrivenIcon from "@/assets/status_utskriven.svg";
    import StatusStrukenIcon from "@/assets/status_struken.svg";
    import Vardplatsbestallning from "@/tabs/Vardplatsbokning/Vardplatsbestallning.vue";
    import Vardplatsdagsoversikt from "@/tabs/Vardplatsbokning/Vardplatsdagsoversikt.vue";
    import {
        Slutenvardsbesok,
        VardplatsStatus,
        Avvikelser,
        HanteraVardplatsOption,
        VardplatsAction,
    } from "@/tabs/Vardplatsbokning/types";
    import emitter from "tiny-emitter/instance";
    import ModalContent from "@/components/generic/ModalContent.vue";
    import HanteraVardplats from "@/tabs/Vardplatsbokning/HanteraVardplats.vue";
    import StrykVardplats from "@/tabs/Vardplatsbokning/StrykVardplats.vue";
    import { STATUSAR } from "./helpers";

    const props = defineProps<{
        tabId: number;
    }>();

    const { tabId } = toRefs(props);

    defineExpose({
        tabId,
    });

    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(20);
    const filterParams = ref<{
        [key: string]: unknown;
        show_strukna?: boolean;
    }>({
        // BE returnerar strukna per default, om inte show_strukna: false är satt
        show_strukna: false,
    });
    const subsetName = ref<string>("");
    const slutenvardsbesok = computed<Slutenvardsbesok[]>(() =>
        store.getters["slutenvardsbesok/slutenvardsbesokSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
            prependNewlyCreated: false,
        })
    );

    // Modaler

    const showHanteraVardplats = ref<boolean>(false);

    const showStrykVardplats = ref<boolean>(false);

    const closeModal = (action: VardplatsAction) => {
        hanteraVardplatsOption.value = HanteraVardplatsOption.DEFAULT; // Resetta tillbaka hanteraVardplatsOption till default värdet

        if (action === VardplatsAction.HANTERA_VARDPLATS) {
            showHanteraVardplats.value = false;
        } else if (action === VardplatsAction.STRYK_VARDPLATS) {
            showStrykVardplats.value = false;
        }
    };

    const hanteraVardplats = (slutenvardsbesok: Slutenvardsbesok) => {
        // selectedItem ska i regel redan vara samma som slutenvardsbesok, men detta är för extra säkerhet
        selectedItem.value = slutenvardsbesok;
        showHanteraVardplats.value = true;
    };

    const strykVardplats = (slutenvardsbesok: Slutenvardsbesok) => {
        selectedItem.value = slutenvardsbesok;
        showStrykVardplats.value = true;
    };

    const skrivInPatienten = (slutenvardsbesok: Slutenvardsbesok) => {
        selectedItem.value = slutenvardsbesok;
        hanteraVardplatsOption.value = HanteraVardplatsOption.SKRIV_IN;
        showHanteraVardplats.value = true;
    };

    const skrivUtPatient = (slutenvardsbesok: Slutenvardsbesok) => {
        selectedItem.value = slutenvardsbesok;
        hanteraVardplatsOption.value = HanteraVardplatsOption.SKRIV_UT;
        showHanteraVardplats.value = true;
    };

    const selectedItem = ref<Slutenvardsbesok | undefined>();
    const hanteraVardplatsOption = ref<HanteraVardplatsOption>(HanteraVardplatsOption.DEFAULT);

    const columns = ref([
        {
            title: "Vårdplats",
            name: "sang.namn",
            sort: "sang",
            lock: "left",
        },
        {
            title: "Avdelning",
            name: "sang.sal.vardavdelning.alias",
            sort: "sang__sal__vardavdelning__alias",
        },
        {
            title: "PersonID",
            name: "patient.personId_formatted",
            sort: "patient__personId",
        },
        {
            title: "Patientnamn",
            name: "patient.namn_reverse",
            sort: "patient__namn",
        },
        {
            title: "Operatör",
            name: "vardplatsbestallning.operator.namn",
            sort: "vardplatsbestallning__operator",
        },
        {
            title: "Vårdgivare",
            name: "vardplatsbestallning.vardgivare.alias",
            sort: "vardplatsbestallning__vardgivare__alias",
        },
        {
            title: "Vårdform",
            name: "vardplatsbestallning.vardform.namn",
            sort: "vardplatsbestallning__vardform",
        },
        {
            title: "Typoperation/Opkod",
            name: "vardplatsbestallning.typoperation",
            trimOverflow: true,
            // TODO: Hitta en lösning (helst med CSS) för att trimma texten till två rader med "..."
            getContent: (item: Slutenvardsbesok) => {
                if (item.vardplatsbestallning.typoperation) {
                    return item.vardplatsbestallning.typoperation;
                } else {
                    return `${item.vardplatsbestallning.huvudoperationskod.code} ${item.vardplatsbestallning.huvudoperationskod.displayName}`;
                }
            },
            // TODO: Hur sorterar vi på denna?
        },
        {
            title: "Info till vårdavdelningen",
            name: "vardplatsbestallning.info_till_vardavdelningen",
            sort: "vardplatsbestallning__info_till_vardavdelningen",
            trimOverflow: true,
            // TODO: Hitta en lösning (helst med CSS) för att trimma texten till två rader med "..."
            getTooltipText: (item: Slutenvardsbesok) => {
                return item.vardplatsbestallning.info_till_vardavdelningen;
            },
        },
        {
            title: "Opdatum",
            name: "vardplatsbestallning.operationsdatum",
            sort: "vardplatsbestallning__operationsdatum",
        },
        {
            title: "Inställelsetid",
            name: "vardplatsbestallning.installelsetid",
            sort: "vardplatsbestallning__installelsetid",
            type: "time",
            // TODO: Flagga om det saknas
        },
        {
            title: "Indatum",
            name: "indatum",
            sort: "indatum",
        },
        {
            title: "Intid",
            name: "intid",
            sort: "intid",
            type: "time",
        },
        {
            title: "Utdatum",
            name: "utdatum",
            sort: "utdatum",
        },
        {
            title: "Uttid",
            name: "uttid",
            sort: "uttid",
            type: "time",
        },
        {
            title: "Inskriven",
            name: "inskriven_tidpunkt",
            sort: "inskriven_tidpunkt",
            getContent: (item: Slutenvardsbesok) => {
                if (item.inskriven_tidpunkt) {
                    return moment(item.inskriven_tidpunkt).format("HH:mm");
                }
            },
        },
        {
            title: "Utskriven",
            name: "utskriven_tidpunkt",
            sort: "utskriven_tidpunkt",
            getContent: (item: Slutenvardsbesok) => {
                if (item.utskriven_tidpunkt) {
                    return moment(item.utskriven_tidpunkt).format("HH:mm");
                }
            },
        },
        {
            title: "Avvikelser",
            name: "avvikelser",
            sort: "avvikelser",
        },
        {
            title: "Status",
            name: "status",
            icons: {
                Beställd: StatusBestalldIcon,
                Bokad: StatusBokadIcon,
                Inskriven: StatusInskrivenIcon,
                Utskriven: StatusUtskrivenIcon,
                Struken: StatusStrukenIcon,
            },
            sort: "status__sortering",
            lock: "right",
        },
    ]);

    const dropdownActions = computed(() => {
        return [
            {
                title: "Hantera vårdplats",
                name: "hanteraVardplats",
                show: (item: Slutenvardsbesok) =>
                    [
                        VardplatsStatus.BESTALLD,
                        VardplatsStatus.BOKAD,
                        VardplatsStatus.INSKRIVEN,
                        VardplatsStatus.UTSKRIVEN,
                    ].includes(item.status),
                icon: ActionIconBoka,
            },
            {
                title: "Vårdplatsbeställning",
                name: "openVardplatsbestallning",
                show: (item: Slutenvardsbesok) =>
                    [
                        VardplatsStatus.BESTALLD,
                        VardplatsStatus.BOKAD,
                        VardplatsStatus.INSKRIVEN,
                        VardplatsStatus.UTSKRIVEN,
                    ].includes(item.status),
                icon: ActionIconVardplatsbestallning,
            },
            {
                title: "Stryk beställning",
                name: "strykVardplats",
                show: (item: Slutenvardsbesok) =>
                    [VardplatsStatus.BESTALLD, VardplatsStatus.BOKAD].includes(item.status),
                icon: ActionIconStryk,
            },
            {
                title: "Skriv in patienten",
                name: "skrivInPatienten",
                show: (item: Slutenvardsbesok) => item.status === VardplatsStatus.BOKAD,
                icon: ActionIconSkrivIn,
            },
            {
                title: "Skriv ut patient",
                name: "skrivUtPatient",
                show: (item: Slutenvardsbesok) => item.status === VardplatsStatus.INSKRIVEN,
                icon: ActionIconSkrivUt,
            },
        ];
    });

    const sort = async (sort: string) => {
        filterParams.value = {
            ...(filterParams.value ? filterParams.value : {}),
            ordering: sort,
        };

        await fetchSlutenvardsbesok({
            page: 1,
        });
    };

    const loadPage = async (page: number) => {
        await fetchSlutenvardsbesok({ page });
    };

    const changeLimit = async (value: number) => {
        listLimit.value = value;

        await fetchSlutenvardsbesok({
            limit: value,
            page: 1,
        });
    };

    // Sökfiltrering

    const filterValues = ref<any>({});

    const filterFields = computed(() => ({
        q: { label: "Sök" },
        indatum__gte: { label: "Indatum från" },
        indatum__lte: { label: "Indatum till" },
        utdatum__gte: { label: "Utdatum från" },
        utdatum__lte: { label: "Utdatum till" },
        patient__personId: { label: "PersonID" },
        patient__namn: { label: "Patientnamn" },
        vardplatsbestallning__operator: { label: "Operatör", options: vardpersoner.value },
        vardplatsbestallning__vardgivare: { label: "Vårdgivare", options: vardgivare.value },
        vardplatsbestallning__vardform: { label: "Vårdform", options: vardformer.value },
        vardplatsbestallning__huvudoperationskod: { label: "Huvudoperationskod", options: operationskoder.value },
        vardplatsbestallning__huvuddiagnos: { label: "Huvuddiagnos", options: diagnoskoder.value },
        vardplatsbestallning__operationsdatum__gte: { label: "Operationsdatum från" },
        vardplatsbestallning__operationsdatum__lte: { label: "Operationsdatum till" },
        sang__sal__vardavdelning: { label: "Avdelning", options: avdelningar.value },
        sang: { label: "Vårdplats", options: sangar.value },
        status: { label: "Status", options: STATUSAR },
        show_strukna: { label: "Visa strukna" },
        avvikelser: { label: "Avvikelser", options: avvikelser },
    }));

    const handleSearch = async (data: { [key: string]: unknown }) => {
        const filterData = Object.entries(data).filter(([name, value]) => value !== "");

        const { show_strukna, ...params } = setShowStrukna({
            // Sätt till default false om show_strukna inte är satt. Behövs då BE returnerar strukna per default
            value: typeof data.show_strukna === "boolean" ? data.show_strukna : false,
            params: Object.fromEntries(filterData),
        });

        // Sätter inte show_strukna i query params om status redan är satt till att kolla på strukna
        if (params.status === VardplatsStatus.STRUKEN) {
            filterParams.value = params;
            filterValues.value = filterParams.value;
        } else {
            filterParams.value = {
                ...params,
                show_strukna,
            };
            filterValues.value = filterParams.value;
        }

        await fetchSlutenvardsbesok({ page: 1 });
    };
    /**
     * Returnerar snabbfilter för vårdplatsöversikten.
     * Används för att snabbt kunna filtrera innehållet i tabellen.
     *
     */
    const getQuickFilters = () => {
        const currentPersonId = computed(() => store.getters["patientData/personId"]);
        const today = moment().format("YYYY-MM-DD");
        const tomorrow = moment().add(1, "days").format("YYYY-MM-DD");

        return [
            {
                key: "active-patient",
                text: "Aktiv patient",
                isActive: Boolean(
                    currentPersonId.value && filterValues.value?.patient__personId === currentPersonId.value
                ),
                filters: {
                    patient__personId: currentPersonId.value,
                },
                show: !!currentPersonId.value,
            },
            {
                key: "today",
                text: "Idag",
                isActive: filterValues.value.indatum__gte === today && filterValues.value.indatum__lte === today,
                filters: {
                    indatum__gte: today,
                    indatum__lte: today,
                },
                show: true,
            },
            {
                key: "tomorrow",
                text: "Imorgon",
                isActive: filterValues.value.indatum__gte === tomorrow && filterValues.value.indatum__lte === tomorrow,
                filters: {
                    indatum__gte: tomorrow,
                    indatum__lte: tomorrow,
                },
                show: true,
            },
            {
                key: "bestallda",
                text: "Beställda",
                isActive: filterValues.value.status === "Beställd",
                filters: {
                    status: "Beställd",
                },
                show: true,
            },
            {
                key: "inskrivna",
                text: "Inskrivna",
                isActive: filterValues.value.status === "Inskriven",
                filters: {
                    status: "Inskriven",
                },
                show: true,
            },
        ];
    };

    const vardpersoner = ref([]);
    const searchVardperson = async (search: string) => {
        try {
            const response = await klinikenApi.get("/core/externavardpersoner/", getConfig({ params: { q: search } }));
            vardpersoner.value = response.data.results.map((operator: any) => ({
                value: operator.id,
                label: operator.namn,
            }));
        } catch (error) {
            openDialog("Kunde inte hämta operatörer.", "warning");
        }
    };

    const vardgivare = ref([]);
    const searchVardgivare = async (search: string) => {
        try {
            const response = await klinikenApi.get("/core/externavardgivare/", getConfig({ params: { q: search } }));
            vardgivare.value = response.data.results.map((vg: any) => ({
                value: vg.id,
                label: vg.alias,
            }));
        } catch (error) {
            openDialog("Kunde inte hämta vårdgivare.", "warning");
        }
    };

    const vardformer = ref([]);
    const fetchVardformer = async () => {
        try {
            const response = await klinikenApi.get(
                "/operation/kodverk/vardformer/",
                getConfig({ params: { typ: "SLUTENVÅRD" } })
            );
            vardformer.value = response.data.results.map((vf: any) => ({
                value: vf.id,
                label: vf.namn,
            }));
        } catch (error) {
            openDialog("Kunde inte hämta vårdformer.", "warning");
        }
    };

    const operationskoder = ref([]);
    const searchOperationskoder = async (search: string) => {
        if (search.length < 3) {
            return;
        }
        try {
            const response = await klinikenApi.get(
                "/operation/kodverk/operationskoder/",
                getConfig({ params: { q: search } })
            );
            operationskoder.value = response.data.results.map((kod: any) => ({
                value: kod.id,
                label: `${kod.code} ${kod.displayName}`,
            }));
        } catch (error) {
            openDialog("Kunde inte hämta operationskoder.", "warning");
        }
    };

    const diagnoskoder = ref([]);
    const searchDiagnoskoder = async (search: string) => {
        if (search.length < 3) {
            return;
        }
        try {
            const response = await klinikenApi.get("/core/kodverk/icdkoder/", getConfig({ params: { q: search } }));
            diagnoskoder.value = response.data.results.map((kod: any) => {
                return { value: kod.id, label: `${kod.code} ${kod.displayName}` };
            });
        } catch (error) {
            openDialog("Kunde inte hämta diagnoskoder.", "warning");
        }
    };

    const avdelningar = ref([]);
    const searchAvdelningar = async (search: string) => {
        try {
            const response = await klinikenApi.get(
                "/core/orgunits/",
                getConfig({ params: { vardenhetstyp: "slutenvardsavdelning", q: search } })
            );
            avdelningar.value = response.data.results.map((avd: any) => ({
                value: avd.id,
                label: avd.alias,
            }));
        } catch (error) {
            openDialog("Kunde inte hämta avdelningar.", "warning");
        }
    };

    const sangar = ref([]);
    const searchSangar = async (search: string) => {
        try {
            const response = await klinikenApi.get(
                "/slutenvard/sangar/",
                getConfig({ params: { namn__icontains: search } })
            );
            sangar.value = response.data.results.map((sang: any) => ({
                value: sang.id,
                label: sang.namn,
            }));
        } catch (error) {
            openDialog("Kunde inte hämta vårdplatser.", "warning");
        }
    };

    const avvikelser = [{ value: "", label: "" }];
    Object.values(Avvikelser).forEach((value: Avvikelser) => {
        avvikelser.push({ value, label: value });
    });

    const showStrukna = ref(false);

    /**
     * Sätter show_strukna och showStrukna till skickat value. Kommer sätta det i params och returnera, om det skickas
     * med
     */
    const setShowStrukna: (props: {
        value: boolean;
        params?: { [key: string]: unknown };
    }) => typeof filterParams.value = ({ value, params }) => {
        showStrukna.value = value;

        return {
            ...(params ?? filterParams.value),
            show_strukna: value,
        };
    };

    /**
     * Sätter show_strukna i filter params till false eller tar bort det om användaren valt att filterar på status
     * "Struken"
     */
    const changeStatusFilter = (event: any) => {
        if (event.target.value === "Struken") {
            // Rensar bort show_strukna, eftersom filtrering ändå görs på status struken
            const { show_strukna, ...params } = filterParams.value;

            filterParams.value = params;
        } else {
            filterParams.value = {
                ...setShowStrukna({ value: false, params: filterParams.value }),
            };
        }
    };

    const fetchSlutenvardsbesok = async ({
        forceRefresh,
        page,
        resetSubset,
        resetNewlyCreated,
        limit = listLimit.value,
    }: {
        page: number;
        limit?: number;
        forceRefresh?: boolean;
        resetSubset?: boolean;
        resetNewlyCreated?: boolean;
    }) => {
        try {
            /*
                Sätter om page innan resten, för att tillåta rendering av existerande data i store.
                Görs inte om subset ska resettas. Då vill vi inte visa tidigare data
             */
            if (!resetSubset) {
                listPage.value = page;
            }

            // Visar bara laddningsikon om det inte finns någon existerande data i store
            if (!slutenvardsbesok.value.length) {
                store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true });
            }

            const {
                nextPage,
                total,
                subsetName: fetchedSubsetName,
            } = await store.dispatch("slutenvardsbesok/fetchSlutenvardsbesok", {
                page,
                limit,
                resetNewlyCreated,
                /*
                    TODO Ska inte vara hårdkodat till true när vi har en lösning för att invalidera dat i store och
                    göra nya automatiska hämtningar
                 */
                forceRefresh: true,
                filterParams: filterParams.value,
            });

            /*
             * Sätter om page efter hämtning i de fall där subset ska resettas och inte kommer visa tidigare data
             */
            if (resetSubset) {
                listPage.value = page;
            }

            subsetName.value = fetchedSubsetName;

            if (typeof total === "number") {
                listCount.value = total;
            }
        } catch (error) {
            console.error(error);

            openDialog("Kunde inte hämta vårdplatsbeställningar.", "warning");
        } finally {
            store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
        }
    };

    const setActivePatient = async (slutenvardsbesok: Slutenvardsbesok) => {
        if (slutenvardsbesok.patient) {
            await store.dispatch("patientData/loadFromBackend", {
                id: slutenvardsbesok.patient.id,
                failSilently: false,
            });
            selectedItem.value = slutenvardsbesok;

            store.dispatch("alertInformation/loadAlertInformationSymbol", { patientId: slutenvardsbesok.patient.id });
            store.dispatch("alertInformation/loadAlertInformationList", { patientId: slutenvardsbesok.patient.id });
        }
    };

    const dagsoversikt = () => {
        store.dispatch("tabs/openTab", {
            component: Vardplatsdagsoversikt,
        });
    };

    const openVardplatsbestallning = async (slutenvardsbesok: Slutenvardsbesok) => {
        await store.dispatch("tabs/closeTabWithUnsavedDataCheck", Vardplatsbestallning, { root: true });
        store.dispatch("tabs/openTab", {
            component: Vardplatsbestallning,
            parameters: { id: slutenvardsbesok.vardplatsbestallning.id },
        });
    };

    onMounted(async () => {
        emitter.on(`refreshTab-${tabId?.value}`, async () => {
            await fetchSlutenvardsbesok({
                page: listPage.value,
                limit: listLimit.value,
                forceRefresh: true,
                resetSubset: true,
                resetNewlyCreated: true,
            });
        });
    });

    onUnmounted(() => {
        emitter.off(`refreshTab-${tabId?.value}`);
    });

    onBeforeMount(async () => {
        store.commit("vardplatsbestallning/addSubscriber");
        store.commit("slutenvardsbesok/addSubscriber");
        await fetchVardformer();
        await fetchSlutenvardsbesok({ page: listPage.value });
    });

    onBeforeUnmount(() => {
        store.commit("vardplatsbestallning/removeSubscriber");
        store.commit("slutenvardsbesok/removeSubscriber");
    });
</script>

<template>
    <div class="vardplatsoversikt">
        <div class="header">
            <h2>Vårdplatsöversikt</h2>
            <KButton @click="dagsoversikt" variant="link">Dagsöversikt</KButton>
        </div>

        <SearchFilter id="search-filter" v-model="filterValues" :fields="filterFields" @input="handleSearch">
            <template v-slot:header-filters>
                <KText class="search-field" name="q" placeholder="Sök" />
            </template>

            <template v-slot:quick-filters>
                <a
                    v-for="filter in getQuickFilters()"
                    :key="filter.key"
                    :class="{ active: filter.isActive }"
                    @click="handleSearch({ ...filter.filters })"
                    v-show="filter.show"
                >
                    {{ filter.text }}
                </a>
            </template>
            <template v-slot:fields>
                <FormulateInput type="datepicker" name="indatum__gte" label="Indatum från" />
                <FormulateInput type="datepicker" name="indatum__lte" label="Indatum till" />
                <FormulateInput type="datepicker" name="utdatum__gte" label="Utdatum från" />
                <FormulateInput type="datepicker" name="utdatum__lte" label="Utdatum till" />
                <KText name="patient__personId" label="PersonID" />
                <KText name="patient__namn" label="Patientnamn" />
                <FormulateInput
                    type="vueselect"
                    name="vardplatsbestallning__operator"
                    label="Operatör"
                    :options="vardpersoner"
                    @search="searchVardperson"
                    class="autocomplete"
                />
                <FormulateInput
                    type="vueselect"
                    name="vardplatsbestallning__vardgivare"
                    label="Vårdgivare"
                    :options="vardgivare"
                    @search="searchVardgivare"
                    class="autocomplete"
                />
                <FormulateInput
                    type="vueselect"
                    name="vardplatsbestallning__vardform"
                    label="Vårdform"
                    :options="vardformer"
                />
                <FormulateInput
                    type="vueselect"
                    name="vardplatsbestallning__huvudoperationskod"
                    label="Huvudoperationskod"
                    :options="operationskoder"
                    @search="searchOperationskoder"
                    class="autocomplete"
                />
                <FormulateInput
                    type="vueselect"
                    name="vardplatsbestallning__huvuddiagnos"
                    label="Huvuddiagnos"
                    :options="diagnoskoder"
                    @search="searchDiagnoskoder"
                    class="autocomplete"
                />
                <FormulateInput
                    type="datepicker"
                    name="vardplatsbestallning__operationsdatum__gte"
                    label="Operationsdatum från"
                />
                <FormulateInput
                    type="datepicker"
                    name="vardplatsbestallning__operationsdatum__lte"
                    label="Operationsdatum till"
                />
                <FormulateInput
                    type="vueselect"
                    name="sang__sal__vardavdelning"
                    label="Avdelning"
                    :options="avdelningar"
                    @search="searchAvdelningar"
                    class="autocomplete"
                />
                <FormulateInput
                    type="vueselect"
                    name="sang"
                    label="Vårdplats"
                    :options="sangar"
                    @search="searchSangar"
                    class="autocomplete"
                />
                <!-- TODO: Avvikelser -->
                <FormulateInput
                    type="vueselect"
                    name="status"
                    label="Status"
                    :options="STATUSAR"
                    @change="changeStatusFilter"
                />
                <FormulateInput name="show_strukna" type="checkbox" v-model="showStrukna" label="Visa strukna" />
                <FormulateInput
                    v-if="showStrukna"
                    type="select"
                    name="avvikelser"
                    label="Avvikelser"
                    :options="avvikelser"
                />
            </template>
        </SearchFilter>

        <div class="subheader">
            <span>
                Visar <strong>{{ slutenvardsbesok.length }}</strong> av <strong>{{ listCount }}</strong> träffar
            </span>
        </div>

        <KlinikenTable
            :columns="columns"
            :items="slutenvardsbesok"
            :selectedItem="selectedItem"
            paginationStyle="paging"
            :count="listCount"
            :limit="listLimit"
            :currentPage="listPage"
            :lockColumns="true"
            :hasDropdown="true"
            :dropdown-actions="dropdownActions"
            @sort="sort"
            @loadPage="loadPage"
            @changeLimit="changeLimit"
            @tableRowClicked="setActivePatient"
            @openVardplatsbestallning="openVardplatsbestallning"
            @hanteraVardplats="hanteraVardplats"
            @strykVardplats="strykVardplats"
            @skrivInPatienten="skrivInPatienten"
            @skrivUtPatient="skrivUtPatient"
        />
        <ModalContent
            title="Stryk vårdplats"
            v-if="showStrykVardplats && selectedItem"
            @close="closeModal(VardplatsAction.STRYK_VARDPLATS)"
            showCloseButton
        >
            <StrykVardplats
                :slutenvardsbesokId="selectedItem.id"
                @close="closeModal(VardplatsAction.STRYK_VARDPLATS)"
            />
        </ModalContent>
        <ModalContent
            title="Hantera vårdplats"
            v-if="showHanteraVardplats && selectedItem"
            @close="closeModal(VardplatsAction.HANTERA_VARDPLATS)"
            showCloseButton
        >
            <HanteraVardplats
                :slutenvardsbesokId="selectedItem.id"
                :option="hanteraVardplatsOption"
                @close="closeModal(VardplatsAction.HANTERA_VARDPLATS)"
            />
        </ModalContent>
    </div>
</template>

<style lang="scss">
    .vardplatsoversikt {
        .header {
            display: flex;
            gap: 20px;
        }
    }
</style>
